/**
 * StarCelebration Component
 *
 * Brief full-screen overlay shown when a user earns a star.
 * Displays for 2.5 seconds with a glow animation.
 */

import React from 'react';
import { Box, Typography, Fade, alpha, useTheme } from '@mui/material';
import { Star } from '@mui/icons-material';
import { keyframes } from '@mui/system';

interface StarCelebrationProps {
  visible: boolean;
  taskTitle: string | null;
  starsEarned: number;
  totalStars: number;
  onDismiss: () => void;
}

const pulseGlow = keyframes`
  0%, 100% {
    transform: scale(1);
    filter: drop-shadow(0 0 8px rgba(16, 185, 129, 0.6));
  }
  50% {
    transform: scale(1.1);
    filter: drop-shadow(0 0 20px rgba(16, 185, 129, 0.9));
  }
`;

const StarCelebration: React.FC<StarCelebrationProps> = ({
  visible,
  taskTitle,
  starsEarned,
  totalStars,
  onDismiss,
}) => {
  const theme = useTheme();

  if (!visible) return null;

  return (
    <Fade in={visible} timeout={300}>
      <Box
        onClick={onDismiss}
        sx={{
          position: 'fixed',
          inset: 0,
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: alpha(theme.palette.background.default, 0.95),
          backdropFilter: 'blur(8px)',
          cursor: 'pointer',
        }}
      >
        {/* Star icon with glow animation */}
        <Box
          sx={{
            animation: `${pulseGlow} 1.5s ease-in-out infinite`,
            mb: 3,
          }}
        >
          <Star
            sx={{
              fontSize: 80,
              color: theme.palette.success.main, // Green
            }}
          />
        </Box>

        {/* Task Complete text */}
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            color: 'text.primary',
            mb: 1,
          }}
        >
          Task Complete!
        </Typography>

        {/* Task title */}
        {taskTitle && (
          <Typography
            variant="body1"
            sx={{
              color: 'text.secondary',
              mb: 3,
            }}
          >
            {taskTitle}
          </Typography>
        )}

        {/* Progress stars */}
        <Box sx={{ display: 'flex', gap: 0.75, mb: 2 }}>
          {Array.from({ length: totalStars }).map((_, i) => (
            <Star
              key={i}
              sx={{
                fontSize: 24,
                color: i < starsEarned ? theme.palette.success.main : alpha(theme.palette.text.primary, 0.2),
                transition: 'color 0.3s ease',
              }}
            />
          ))}
        </Box>

        {/* Progress text */}
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            fontWeight: 500,
          }}
        >
          {starsEarned}/{totalStars} Stars
        </Typography>

        {/* Tap to dismiss hint */}
        <Typography
          variant="caption"
          sx={{
            color: 'text.disabled',
            mt: 4,
          }}
        >
          Tap anywhere to continue
        </Typography>
      </Box>
    </Fade>
  );
};

export default StarCelebration;
